import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, ChevronDown, Siren, RotateCcw } from "lucide-react";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = [
  {
    id: "urgent",
    label: "Urgent / Last-Minute",
    desc: "Active crisis, failing deadline, sudden PM vacancy — triaged first.",
  },
  {
    id: "standard",
    label: "Standard Engagement",
    desc: "Upcoming kickoff, scheduled PM transition, planned delivery support.",
  },
  {
    id: "audit",
    label: "Strategic / AI Audit",
    desc: "Feasibility review, program health check, AI readiness evaluation.",
  },
];

const INDUSTRIES = [
  "Healthcare",
  "Tech",
  "Finance",
  "Media",
  "Marketing",
  "Sales Plan",
  "AI Project Implementation",
  "Other",
];

const PROJECT_TYPES = [
  "Project Rescue & Assurance",
  "End-to-End PM (Short Term)",
  "End-to-End PM (Long Term)",
  "AI Readiness Assessment",
  "AI Implementation Program",
  "Quick-Turnaround / Audit",
];

const STEPS = [
  { n: "01", t: "Submit the brief", d: "Two minutes. Tell us what's happening on the ground." },
  { n: "02", t: "We triage by urgency", d: "Urgent and last-minute requests jump the queue." },
  { n: "03", t: "A commander engages", d: "A senior PM matched to your industry and problem." },
];

const fieldCls =
  "w-full rounded-lg border border-white/10 bg-abyss/70 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-all duration-300 focus:border-gold/60 focus:ring-2 focus:ring-gold/20";

const inputCls = fieldCls;
const selectWrap = "relative";

function Select({ children, ...props }) {
  return (
    <div className={selectWrap}>
      <select {...props} className={`${fieldCls} appearance-none pr-10`}>
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
    </div>
  );
}

export default function ContactSection() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    company: "",
    industry: "Healthcare",
    project_type: "Project Rescue & Assurance",
    message: "",
  });
  const [urgency, setUrgency] = useState("urgent");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/enquiries`, { ...form, urgency });
      setResult(data);
      toast.success("Brief transmitted to Astra command", {
        description: `Reference ${data.reference} logged.`,
      });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(
        typeof msg === "string" ? msg : "Transmission failed — check your details and retry.",
        { description: "Your brief was not delivered. Please try again." }
      );
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setResult(null);
    setForm({
      full_name: "",
      email: "",
      company: "",
      industry: "Healthcare",
      project_type: "Project Rescue & Assurance",
      message: "",
    });
    setUrgency("urgent");
  };

  const tier = TIERS.find((t) => t.id === urgency);

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative scroll-mt-24 overflow-hidden py-20 sm:py-28 lg:py-32"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-gold/8 blur-[140px]"
      />

      <div className="relative mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16 lg:px-8">
        <div>
          <Reveal>
            <p className="mb-4 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.28em] text-gold">
              <Siren className="h-4 w-4" />
              Get in touch
            </p>
            <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Request a project manager.
            </h2>
            <p className="mt-6 max-w-md leading-relaxed text-slate-400 sm:text-lg">
              Tell us what's happening — a stalled rollout, a departing PM, an
              AI programme that has to land. Your brief lands directly in Astra
              command, and urgent requests are triaged first.
            </p>
          </Reveal>

          <div className="mt-12 space-y-0">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={0.1 + i * 0.08}>
                <div className="flex gap-6 border-t border-white/8 py-5">
                  <span className="font-mono text-sm text-gold">{s.n}</span>
                  <div>
                    <h3 className="font-medium text-white">{s.t}</h3>
                    <p className="mt-1 text-sm text-slate-500">{s.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.15}>
          <div className="rounded-2xl border border-white/10 bg-plate/70 p-6 backdrop-blur sm:p-8">
            <AnimatePresence mode="wait">
              {result ? (
                <motion.div
                  key="success"
                  data-testid="contact-success-banner"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="flex min-h-[480px] flex-col items-center justify-center text-center"
                >
                  <span className="relative grid h-16 w-16 place-items-center rounded-full border border-gold/40 bg-gold/10">
                    <CheckCircle2 className="h-8 w-8 text-gold" />
                  </span>
                  <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.28em] text-gold">
                    Enquiry received
                  </p>
                  <h3
                    data-testid="contact-thankyou-title"
                    className="mt-4 font-display text-3xl font-bold tracking-tight text-white"
                  >
                    Thank you, {form.full_name.split(" ")[0] || "there"}.
                  </h3>
                  <p className="mt-2 font-mono text-xs tracking-[0.2em] text-slate-500">
                    Reference {result.reference}
                  </p>
                  <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate-400">
                    Your brief has been marked{" "}
                    <span className="text-gold">{tier?.label}</span> and is with
                    our team. We'll reach out to you as soon as possible.
                  </p>
                  <button
                    data-testid="contact-submit-another-button"
                    onClick={reset}
                    className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300 transition-all hover:border-white/40 hover:bg-white/5"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Submit another brief
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  data-testid="contact-enquiry-form"
                  onSubmit={submit}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.4 }}
                  className="space-y-5"
                >
                  <div>
                    <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
                      Urgency & timeline <span className="text-gold">*</span>
                    </p>
                    <div
                      role="radiogroup"
                      aria-label="Urgency and timeline"
                      data-testid="contact-select-urgency"
                      className="grid gap-2 sm:grid-cols-3"
                    >
                      {TIERS.map((t) => {
                        const active = urgency === t.id;
                        return (
                          <button
                            key={t.id}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            data-testid={`urgency-option-${t.id}`}
                            onClick={() => setUrgency(t.id)}
                            className={`rounded-lg border p-3.5 text-left transition-all duration-300 ${
                              active
                                ? "border-gold/60 bg-gold/10 shadow-[0_0_24px_rgba(226,185,92,0.15)]"
                                : "border-white/10 bg-abyss/60 hover:border-white/25"
                            }`}
                          >
                            <span
                              className={`block text-xs font-semibold ${
                                active ? "text-gold" : "text-slate-200"
                              }`}
                            >
                              {t.label}
                            </span>
                            <span className="mt-1.5 block text-[11px] leading-snug text-slate-500">
                              {t.desc}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="f-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                        Full name / role <span className="text-gold">*</span>
                      </label>
                      <input
                        id="f-name"
                        data-testid="contact-input-name"
                        required
                        minLength={2}
                        value={form.full_name}
                        onChange={set("full_name")}
                        placeholder="Alexandra Reyes, COO"
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label htmlFor="f-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                        Corporate email <span className="text-gold">*</span>
                      </label>
                      <input
                        id="f-email"
                        type="email"
                        data-testid="contact-input-email"
                        required
                        value={form.email}
                        onChange={set("email")}
                        placeholder="you@company.com"
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label htmlFor="f-company" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                        Organization <span className="text-gold">*</span>
                      </label>
                      <input
                        id="f-company"
                        data-testid="contact-input-company"
                        required
                        minLength={2}
                        value={form.company}
                        onChange={set("company")}
                        placeholder="Company / entity"
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label htmlFor="f-industry" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                        Industry domain
                      </label>
                      <Select
                        id="f-industry"
                        data-testid="contact-select-industry"
                        value={form.industry}
                        onChange={set("industry")}
                      >
                        {INDUSTRIES.map((i) => (
                          <option key={i}>{i}</option>
                        ))}
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="f-type" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                      Core need
                    </label>
                    <Select
                      id="f-type"
                      data-testid="contact-select-project-type"
                      value={form.project_type}
                      onChange={set("project_type")}
                    >
                      {PROJECT_TYPES.map((p) => (
                        <option key={p}>{p}</option>
                      ))}
                    </Select>
                  </div>

                  <div>
                    <label htmlFor="f-msg" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                      Project brief & current bottlenecks <span className="text-gold">*</span>
                    </label>
                    <textarea
                      id="f-msg"
                      data-testid="contact-textarea-message"
                      required
                      minLength={10}
                      rows={4}
                      value={form.message}
                      onChange={set("message")}
                      placeholder="What's happening, where it's stuck, and when it must land."
                      className={`${inputCls} resize-none`}
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-form-submit-button"
                    disabled={sending}
                    className="group flex w-full items-center justify-center gap-3 rounded-full bg-gold px-6 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-obsidian transition-all duration-300 hover:bg-gold-hover hover:shadow-[0_0_40px_rgba(226,185,92,0.45)] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {sending ? (
                      <>
                        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-obsidian/30 border-t-obsidian" />
                        Transmitting…
                      </>
                    ) : (
                      <>
                        Submit emergency PM request
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </>
                    )}
                  </button>
                  <p className="text-center font-mono text-[9px] uppercase tracking-[0.2em] text-slate-600">
                    Routed directly to Astra command — no bots, no queues
                  </p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
